import type { SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { CreationSourceEnum } from 'js/src/client';
import { getVideoCreationSource } from 'js/src/data/app';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getVoicedrivenSceneDuration,
    runVoicedrivenTranscriptBatchOperation,
} from 'js/src/luminary/voicedriven-scenes';

// Returns the playbackRate override to apply to a clip so it exactly fills its
// scene without looping, or null when the inputs are degenerate (skip). A rate
// below 1 slows the clip down to fill a longer scene; a rate of 1 pins natural
// speed (the clip is longer than the scene and simply truncates). The lower
// bound mirrors luminary's playbackRate setter, which rejects values below
// 0.0625 — without the clamp an extreme scene/clip ratio would throw.
export function computePlaybackRateOverride(
    clipDurationMs: number,
    sceneDurationMs: number,
): number | null {
    if (sceneDurationMs <= 0 || clipDurationMs <= 0) {
        return null;
    }
    return Math.min(1, Math.max(0.0625, clipDurationMs / sceneDurationMs));
}

// Walks a scene group and recomputes the playbackRate override on every editable
// video clip so it fills its scene exactly. Recomputing in both directions means
// a clip previously slowed for a long voiceover is reset to natural speed when a
// later voiceover is shorter. MUST run inside runVoicedrivenTranscriptBatchOperation for
// the scene group so scene durations are settled.
export function syncGenxClipPlaybackRatesForSceneGroup(sceneGroup: SceneGroup): void {
    for (const scene of sceneGroup.scenes) {
        const sceneDurationMs = getVoicedrivenSceneDuration(scene);
        for (const asset of scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE)) {
            if (!asset.isEditable || !asset.isVideo || asset.duration <= 0) {
                continue;
            }
            const playbackRate = computePlaybackRateOverride(asset.duration, sceneDurationMs);
            if (playbackRate !== null) {
                asset.playbackRate = playbackRate;
            }
        }
    }
}

// GenX only: after a voice swap or transcript regen grows scene durations, the
// clips' stale playbackRate overrides no longer fill their scenes and luminary
// loops them. Recompute the overrides so each clip is slowed to fit (or pinned
// to natural speed) without regenerating — automating the manual "Advanced
// editing" speed fix. No-op for non-GenX videos.
export const syncGenxClipPlaybackRatesOperation = createLuminaryOperation(
    async ({ luminary, getState, call }, sceneGroupId: string) => {
        const source = getVideoCreationSource(getState());
        if (source !== CreationSourceEnum.GENX && source !== CreationSourceEnum.GENX_REPEAT) {
            return;
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        // Clip durations are only known once the underlying media has loaded.
        await sceneGroup.root.onCanPlay();
        call(runVoicedrivenTranscriptBatchOperation, sceneGroupId, () => {
            syncGenxClipPlaybackRatesForSceneGroup(sceneGroup);
        });
    },
);
